import { LEGAL_VERSION } from "@usales/contract/values";
import "./legal.css";

/**
 * 《用户协议》《隐私政策》。公开页面(/terms、/privacy),不用登录就能看 ——
 * 登录页要求「先阅读并同意」,没登录的人必须能读到全文。
 *
 * 正文里写的每一件事都要和系统的真实行为一致:留存天数、第三方服务、存储地点、
 * Cookie 只有一个会话。**改了这些行为就要回来改这里,并更新 LEGAL_VERSION**
 * (登录时会记下每个人同意的是哪一版)。
 *
 * B2B 的角色划分:账号信息由我们处理;企业客户录入的客户资料、沟通录音,
 * 处理者是企业客户,我们是受托处理(个保法第二十一条)。两份文本都按这个口径写。
 */

const COMPANY = "杭州网利信息技术有限公司";
const EMAIL = "contact@usales.cn";
const EFFECTIVE = LEGAL_VERSION.replace(/^(\d+)-(\d+)-(\d+)$/, (_, y: string, m: string, d: string) => `${y} 年 ${Number(m)} 月 ${Number(d)} 日`);

export const LEGAL_PATHS = { terms: "/terms", privacy: "/privacy" } as const;
export type LegalDoc = keyof typeof LEGAL_PATHS;

export function legalDocFromPath(pathname: string): LegalDoc | undefined {
  return (Object.keys(LEGAL_PATHS) as LegalDoc[]).find((k) => LEGAL_PATHS[k] === pathname.replace(/\/$/, ""));
}

export function LegalPage({ doc }: { doc: LegalDoc }): React.JSX.Element {
  return (
    <div className="legal">
      <header className="legal-top">
        <a className="legal-logo" href="/">Usales <span>元销</span></a>
        <nav>
          <a href={LEGAL_PATHS.terms} aria-current={doc === "terms" ? "page" : undefined}>用户协议</a>
          <a href={LEGAL_PATHS.privacy} aria-current={doc === "privacy" ? "page" : undefined}>隐私政策</a>
        </nav>
      </header>
      <article className="legal-body">
        {doc === "terms" ? <Terms /> : <Privacy />}
      </article>
    </div>
  );
}

function Meta({ title }: { title: string }): React.JSX.Element {
  return (
    <>
      <h1>{title}</h1>
      <p className="legal-meta">生效日期:{EFFECTIVE} · 运营方:{COMPANY}</p>
    </>
  );
}

function Terms(): React.JSX.Element {
  return (
    <>
      <Meta title="Usales 元销用户协议" />
      <p>欢迎使用 Usales 元销(以下简称「本服务」)。本服务由{COMPANY}(以下简称「我们」)提供。
        本协议是你所在的企业或组织(以下简称「企业客户」)及其开通账号的使用人(以下简称「你」)与我们之间关于使用本服务的约定。
        <b>请在登录前仔细阅读,尤其是加粗的条款。你勾选同意并登录,即表示你已阅读并同意本协议和《隐私政策》。</b></p>

      <h2>一、服务内容</h2>
      <p>本服务是面向企业销售团队的协作系统,包括客户名单导入与分级、沟通记录(文本、录音、文件)的转写与整理、
        客户阶段判断、行动建议、知识库检索与 AI 助手等功能。具体功能以系统实际提供的为准,我们可能根据产品迭代调整。</p>
      <p>本服务目前处于试用阶段,暂不收费。如将来收费,我们会提前通知企业客户,并另行约定。</p>

      <h2>二、账号</h2>
      <ol>
        <li>账号由企业客户的管理员开通、分配角色与权限,也由管理员停用。我们不面向个人开放注册。</li>
        <li>首次登录或口令被重置后,你需要先修改口令才能使用。请妥善保管口令,不要与他人共用账号。
          <b>通过你的账号进行的操作视为你本人的操作。</b>发现账号被盗用请立即修改口令并通知管理员。</li>
        <li>你离职或岗位变动时,企业客户可将你名下的客户交接给他人并停用你的账号。</li>
      </ol>

      <h2>三、使用规范</h2>
      <p>你承诺遵守中华人民共和国法律法规,不利用本服务从事以下行为:</p>
      <ol>
        <li><b>在未告知沟通对方的情况下上传通话或会议录音。</b>上传录音前,你应已按《个人信息保护法》告知对方并取得必要的同意;系统在上传时会要求你确认这一点。</li>
        <li>收集、上传非法获取的个人信息,或将本服务中的客户信息用于约定之外的用途。</li>
        <li>上传违法、侵权或含有恶意代码的内容。</li>
        <li>尝试越权访问其他用户或其他企业的数据,对本服务进行破坏、攻击、反向工程,或以自动化手段大量调用。</li>
      </ol>
      <p>违反上述约定的,我们有权暂停相关账号或企业的服务,并保留追究责任的权利。</p>

      <h2>四、企业数据</h2>
      <ol>
        <li><b>企业客户在本服务中录入、上传、生成的数据(包括客户资料、沟通记录、录音、知识库资料)归企业客户所有。</b>
          我们仅按企业客户的使用需要处理这些数据,不出售,也不用于本服务之外的目的。</li>
        <li>对于其中的个人信息,企业客户是个人信息处理者,我们作为受托方按《隐私政策》的说明处理。</li>
        <li>录音、原始文件默认保存 180 天(企业客户可调整)后自动删除,转写文本与整理结果在企业客户删除或服务终止前保留。</li>
        <li>服务终止后,我们将删除相关数据(法律法规要求保留的除外)。</li>
      </ol>

      <h2>五、AI 生成内容</h2>
      <ol>
        <li>本服务使用人工智能模型生成转写、摘要、阶段判断、行动建议、消息草稿等内容。<b>这些内容可能存在错误或遗漏,仅供参考,
          不构成对销售结果的保证。</b>你应结合实际情况自行判断,并对你据此采取的行动负责。</li>
        <li>涉及金额、合同的动作,系统始终只给出建议,需要由人确认后执行。</li>
        <li>每个企业的 AI 用量设有月度额度,达到额度后相关 AI 功能将暂停至次月或调整额度后恢复。</li>
      </ol>

      <h2>六、服务的变更、中断与终止</h2>
      <ol>
        <li>我们会尽力保障服务稳定,但可能因系统维护、升级、网络或第三方服务故障、不可抗力等原因出现中断。
          <b>试用阶段不提供服务可用性承诺。</b>计划内维护我们会尽量提前通知。</li>
        <li>企业客户可随时停止使用本服务。企业客户严重违反本协议的,我们可暂停或终止服务。</li>
      </ol>

      <h2>七、知识产权</h2>
      <p>本服务的软件、界面设计、文档等知识产权归我们所有。企业数据的权利归属按第四条处理。未经许可,不得复制、修改、出售本服务或其任何部分。</p>

      <h2>八、责任限制</h2>
      <p>在法律允许的范围内,对于因使用或无法使用本服务导致的间接损失、商业机会损失,我们不承担责任。
        因我们的过错给你造成直接损失的,我们依法承担相应责任。</p>

      <h2>九、协议的修改</h2>
      <p>我们可能修改本协议。修改后的协议会在本页面公布并更新生效日期;涉及你重要权利义务的修改,
        你会在下次登录时被要求重新阅读并同意。</p>

      <h2>十、法律适用与争议解决</h2>
      <p>本协议适用中华人民共和国法律。因本协议产生的争议,双方应友好协商;协商不成的,提交我们住所地有管辖权的人民法院诉讼解决。</p>

      <h2>十一、联系我们</h2>
      <p>{COMPANY} · 邮箱 <a href={`mailto:${EMAIL}`}>{EMAIL}</a></p>
    </>
  );
}

function Privacy(): React.JSX.Element {
  return (
    <>
      <Meta title="Usales 元销隐私政策" />
      <p>{COMPANY}(以下简称「我们」)重视个人信息保护。本政策说明在你使用 Usales 元销(以下简称「本服务」)时,
        我们如何处理个人信息、你享有哪些权利。<b>请在登录前仔细阅读。</b></p>

      <h2>一、我们的身份</h2>
      <p>本服务是企业服务。根据处理的信息不同,我们的身份不同:</p>
      <ul>
        <li><b>账号信息</b>(你的姓名、邮箱等):我们是个人信息处理者,按本政策处理。</li>
        <li><b>企业客户录入的业务数据</b>(其客户的联系人、电话、沟通记录、录音等):处理者是你所在的企业客户,
          我们受企业客户委托、按其指示处理,不用于其他目的。这些信息涉及的个人(例如企业客户的客户)如需行使权利,
          可联系相应企业客户,我们会配合其处理。</li>
      </ul>

      <h2>二、我们处理哪些信息</h2>
      <ol>
        <li><b>账号信息</b>:姓名、邮箱、所属部门、角色,由企业管理员开通账号时填写;口令只以不可逆的哈希形式保存,我们无法看到你的口令原文。</li>
        <li><b>登录与安全信息</b>:登录时间、会话信息,以及为防止暴力破解而使用的 IP 地址。</li>
        <li><b>业务数据</b>:你和同事在使用中录入或上传的客户资料、沟通文本、录音、文件、知识库资料,以及系统据此生成的转写、摘要、阶段判断和行动建议,
          你对建议的采纳、修改记录。</li>
        <li><b>操作与运行记录</b>:为保障安全和排查问题所必需的操作记录、模型调用记录(调用时间、用途、用量,不含业务内容全文)。</li>
      </ol>
      <p>我们不收集你的设备通讯录、位置、相册等与本服务无关的信息。</p>

      <h2>三、处理目的</h2>
      <ul>
        <li>登录认证、权限控制、账号安全(包括登录限流、强制修改初始口令);</li>
        <li>提供服务功能:转写录音、整理沟通要点、判断客户阶段、生成行动建议、检索知识库、回答助手问题;</li>
        <li>统计 AI 用量以控制额度;保障系统运行、备份和故障排查。</li>
      </ul>
      <p><b>我们不会将你或企业客户的数据出售给任何人,也不会用这些数据训练我们自己的模型。</b></p>

      <h2>四、委托第三方处理</h2>
      <p>为实现上述功能,我们委托以下境内服务商处理必要的数据:</p>
      <div className="legal-table">
        <table>
          <thead><tr><th>服务商</th><th>用途</th><th>涉及的数据</th></tr></thead>
          <tbody>
            <tr><td>阿里云计算有限公司(云服务器、对象存储,杭州)</td><td>运行系统、存储数据库与上传的文件</td><td>全部数据</td></tr>
            <tr><td>阿里云计算有限公司(百炼大模型服务)</td><td>录音转写、文本向量化、图片文字识别</td><td>录音、知识库与沟通文本、图片</td></tr>
            <tr><td>杭州深度求索人工智能基础技术研究有限公司(DeepSeek)</td><td>提取沟通要点、判断阶段、生成建议与回答</td><td>沟通文本与相关知识片段</td></tr>
          </tbody>
        </table>
      </div>
      <p>我们只向服务商传输完成该项功能所需的数据,并要求其按约定保护数据安全。除法律法规要求或经你所在企业客户同意外,我们不向其他第三方提供数据。</p>

      <h2>五、存储地点与期限</h2>
      <ol>
        <li><b>所有数据存储在中华人民共和国境内,不出境。</b></li>
        <li>录音、原始文件:默认保存 180 天后自动删除(企业客户可调整);转写文本和整理结果保留到企业客户删除或服务终止。</li>
        <li>登录会话:最长 14 天后失效,过期记录定期清理。</li>
        <li>数据库备份:滚动保留 7 天。</li>
        <li>账号停用或服务终止后,我们将删除相关数据,法律法规要求保留的除外。</li>
      </ol>

      <h2>六、Cookie 与本地存储</h2>
      <p>本服务只使用一个用于保持登录状态的会话 Cookie(仅服务器可读,网页脚本无法读取),不使用广告或第三方统计类 Cookie。
        部分页面会在浏览器的会话存储中临时保存你未提交的编辑内容,关闭标签页即清除。
        主动点击“保存进度”后，未完成的画像问答会按企业与账号保存在当前浏览器；7天未继续后失效，并在下次访问时清除，也可通过“重新建立”清除。已生成的画像草稿仍保存在系统版本历史中。</p>

      <h2>七、安全措施</h2>
      <ul>
        <li>全站 HTTPS 加密传输;口令以加盐哈希保存;会话令牌在服务器端只保存其哈希值;</li>
        <li>不同企业的数据在数据库层隔离;企业内部按角色和数据范围控制谁能看哪些客户;</li>
        <li>每日备份数据库,并验证过备份可以恢复。</li>
      </ul>
      <p>如发生个人信息安全事件,我们将按法律要求及时告知企业客户和受影响的个人,并采取补救措施。</p>

      <h2>八、你的权利</h2>
      <p>你可以查阅、复制、更正、补充、删除你的个人信息,撤回同意,或注销账号:</p>
      <ul>
        <li>修改口令:登录后打开右上角的账户菜单，选择“修改口令”;</li>
        <li>更正姓名等账号信息、注销账号:联系所在企业的管理员;</li>
        <li>其他请求或管理员无法处理的情况:发邮件至 <a href={`mailto:${EMAIL}`}>{EMAIL}</a>,我们会在 15 个工作日内答复。</li>
      </ul>
      <p>撤回同意或注销账号后,你将无法继续使用本服务,但不影响此前基于同意已进行的处理。</p>

      <h2>九、录音与沟通对方的信息</h2>
      <p>上传通话或会议录音前,上传人应已告知沟通对方并取得必要的同意,系统会在上传时要求确认。企业客户应确保其收集客户信息的方式合法。</p>

      <h2>十、未成年人</h2>
      <p>本服务面向企业及其员工,不面向未满 14 周岁的未成年人。</p>

      <h2>十一、政策更新</h2>
      <p>我们可能更新本政策,更新后会在本页面公布并修改生效日期。重大变更时,你会在下次登录时被要求重新阅读并同意。</p>

      <h2>十二、联系我们</h2>
      <p>{COMPANY} · 个人信息保护相关事宜请联系 <a href={`mailto:${EMAIL}`}>{EMAIL}</a></p>
    </>
  );
}
